<template>
  <noscript>
    <iframe
      v-if="env === 'production'"
      :src="`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`"
      height="0"
      width="0"
      style="display: none; visibility: hidden"
    />
  </noscript>

  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>

  <AppOverlay id="overlay" v-bind="overlay" />
  <AppLoader v-if="isLoading" />

  <ClientOnly>
    <TheChatlio />
  </ClientOnly>
</template>

<script setup lang="ts">
// components
import AppLoader from '~/components/redesign/AppLoader.vue';
import AppOverlay from '~/components/redesign/AppOverlay.vue';
import TheChatlio from '~/components/redesign/TheChatlio.vue';

// composable
import { useI18n } from '#i18n';
import { useDayjs } from '#dayjs';
import { storeToRefs } from 'pinia';
import { useAbsoluteUrl } from '~/composables/absoluteUrl';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';
import { useDocumentVisibility } from '@vueuse/core';

// utils
import {
  isString,
  isObject,
  isValidJSON,
  isValidIpInfo,
  removeLocaleFromPath,
  isLocalStorageAvailable,
} from '~/utils';

// store
import useUIStore from '~/stores/ui';
import { useCommonStore } from '~/stores/common';

// services
import { Cookie } from '~/services';

// constants
import { COOKIE_KEYS, LOCALES, LOCALE_STORAGE_KEYS } from '~/constants';

// types
import type { Link } from '@unhead/vue';
import type { IpInfo } from '~/types/api-responses';

const dayjs = useDayjs();
const route = useRoute();
const { t, locale } = useI18n();
const { getAbsoluteUrl } = useAbsoluteUrl();
const {
  public: { env, GTM_ID },
} = useRuntimeConfig();
const { $osano, $ga, $activity } = useNuxtApp();
const localePathWithSlash = useLocalePathWithSlash();
const documentVisibility = useDocumentVisibility();

const uiStore = useUIStore();
const { isLoading, overlay, isScrollLocked } = storeToRefs(uiStore);

const commonStore = useCommonStore();
const { requestIpInfo, setIpInfo } = commonStore;

const canTrackActivity = ref(false);

const osanoConsentValue = ref<string | null>(null);
const updateOsanoConsentValue = () => {
  if (!isLocalStorageAvailable()) return;

  osanoConsentValue.value = localStorage.getItem(
    LOCALE_STORAGE_KEYS.OSANO_CONSENT_MANAGER,
  );
};

const getIpInfoFromCookie = (): IpInfo | undefined => {
  const pureIpInfoFromCookie = Cookie.get(COOKIE_KEYS.IP_INFO);

  if (!isValidJSON(pureIpInfoFromCookie)) return;

  const parsedIpInfoFromCookie = JSON.parse(pureIpInfoFromCookie);

  if (
    !isObject(parsedIpInfoFromCookie) ||
    !isValidIpInfo(parsedIpInfoFromCookie)
  )
    return;

  return parsedIpInfoFromCookie;
};

onMounted(async () => {
  const ipInfoFromCookie = getIpInfoFromCookie();

  if (ipInfoFromCookie) setIpInfo(ipInfoFromCookie);
  else {
    try {
      const response = await requestIpInfo();

      if (response) {
        setIpInfo(response);
        Cookie.set('ip_info', JSON.stringify(response), {
          expires: dayjs().add(7, 'days').toDate(),
        });
      }
    } catch {
      void clearError();
    }
  }
});

onMounted(() => {
  $ga.getSessionId().then(sessionId => {
    if (isString(sessionId)) {
      Cookie.set(COOKIE_KEYS.GTAG_SESSION_ID, sessionId);
    }
  });

  $ga.getClientId().then(clientId => {
    if (isString(clientId)) {
      Cookie.set(COOKIE_KEYS.GTAG_CLIENT_ID, clientId);
    }
  });

  $osano.cm.addEventListener('osano-cm-consent-saved', ({ ANALYTICS }) => {
    canTrackActivity.value = ANALYTICS === 'ACCEPT';

    nextTick(updateOsanoConsentValue);
  });

  window.onbeforeunload = () => {
    if (canTrackActivity.value) {
      $activity.end();
    }
  };

  window.addEventListener('storage', e => {
    if (e.key === LOCALE_STORAGE_KEYS.OSANO_CONSENT_MANAGER) {
      window.location.reload();
    }
  });
});

watch(
  [osanoConsentValue, documentVisibility],
  ([newValue], [oldValue]) => {
    if ([newValue, oldValue].some(value => !isString(value))) return;
    if (newValue === oldValue) return;

    window.location.reload();
  },
  { immediate: true },
);

watch(documentVisibility, updateOsanoConsentValue);

watch(
  [canTrackActivity, route],
  ([canTrack]) => {
    if (import.meta.server) return;

    if (canTrack) {
      $activity.end();
      $activity.start();
    } else {
      Cookie.delete(COOKIE_KEYS.ACTIVITY);
    }
  },
  { immediate: true },
);

watchEffect(() => {
  if (import.meta.server) return;
  document.body.classList[isScrollLocked.value ? 'add' : 'remove']('no-scroll');
});

const link = computed<Link[]>(() => {
  const pathWithoutLocale = removeLocaleFromPath(route.path);

  const links = [
    {
      rel: 'canonical',
      href: getAbsoluteUrl(route.path),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: getAbsoluteUrl(pathWithoutLocale),
    },
  ];

  LOCALES.SUPPORTED_LOCALES.reduce((acc, locale) => {
    const localizedPath = localePathWithSlash(pathWithoutLocale, locale);

    acc.push({
      rel: 'alternate',
      hreflang: locale,
      href: getAbsoluteUrl(localizedPath),
    });

    return acc;
  }, links);

  return links;
});

useHead({
  link,
  title: () => t('index.seo.title'),
  meta: [
    {
      name: 'description',
      content: () => t('index.seo.description'),
    },
    {
      property: 'og:image',
      content:
        'https://objectfirst.nyc3.digitaloceanspaces.com/Metadata/OG_Banner_General_360x169.png',
    },
    {
      property: 'og:url',
      content: () => getAbsoluteUrl(route.path),
    },
  ],
  htmlAttrs: {
    lang: locale,
  },
});

useSchemaOrg([
  defineWebSite({
    image:
      'https://objectfirst.nyc3.digitaloceanspaces.com/Metadata/OG_Banner_General_360x169.png',
  }),
  defineImage({
    url: 'https://objectfirst.b-cdn.net/uploads/logos/logo-white.svg',
    width: 103,
    height: 51,
    caption: 'Object First',
  }),
]);
</script>

<style scoped lang="scss">
[data-component-name='AppOverlay'] {
  padding: 1rem;
}
</style>
